import React, { KeyboardEvent, PropsWithChildren } from 'react'
import clsx from 'clsx'
import Backdrop from './Backdrop'
import Portal from '../Portal'
import { createUseStyles } from '../../lib/styling'
import { grabFocus, preventBackgroundScrolling, restoreBackgroundScrolling, trapFocus } from '../../lib/utils'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(
  (theme: any) => ({
    root: {
      position: 'fixed',
      zIndex: theme.elevation.zindex.modal,
      right: 0,
      bottom: 0,
      top: 0,
      left: 0,
    },
  }),
  'modal',
)

type Props = PropsWithChildren<{
  /** When set to a number the Modal waits before closing.
   * This is useful for instance to let time for anims to complete. */
  closeAfterDelay?: number
  /** If `true`, the backdrop won't be shown. */
  disableBackdrop?: boolean
  /** If `true`, a click on the backdrop won't close the modal. */
  disableBackdropClick?: boolean
  /** If `true`, a press on ESC key won't close the modal. */
  disableEscapeKeyDown?: boolean
  /** Callback fired when the backdrop is clicked. */
  onBackdropClick?: () => void
  /** Called when the modal closes */
  onClose?: () => void
  /** If true, the modal will open */
  open?: boolean
}>

export type ModalProps = Props & CommonProps

type OriginalStyles = {
  originalBodyPad: number
  originalBodyOverflow: string
  isBodyOverflowing: boolean
}

const Modal = ({
  open,
  onClose,
  closeAfterDelay,
  onBackdropClick,
  disableBackdrop,
  disableBackdropClick,
  disableEscapeKeyDown,
  children,
  className,
  ...rest
}: ModalProps) => {
  const classes = useStyles()
  const [exited, setExited] = React.useState(true)
  const [mounted, setMounted] = React.useState(false)
  const rootRef = React.useRef(null)
  const nodeToRestore = React.useRef<HTMLElement | null>()
  const originalStyles = React.useRef<OriginalStyles | null>()

  const handleClose = () => {
    setExited(true)
    setMounted(false)
  }

  React.useEffect(() => {
    if (open) {
      setExited(false)
    } else if (!exited) {
      if (closeAfterDelay) {
        const timer = setTimeout(
          () => handleClose(),
          // let's keep some (100ms) margin to avoid unmounting children too soon
          closeAfterDelay * 1000 + 100,
        )
        return () => clearTimeout(timer)
      } else {
        handleClose()
      }
    }
  }, [open, exited, closeAfterDelay])

  React.useEffect(() => {
    if (open && !mounted) {
      setMounted(true)
    }
  }, [open, mounted])

  React.useEffect(() => {
    if (!mounted) {
      return
    }
    nodeToRestore.current = grabFocus(rootRef.current) as HTMLElement
    originalStyles.current = preventBackgroundScrolling()

    return () => {
      if (nodeToRestore.current && nodeToRestore.current.focus) {
        nodeToRestore.current.focus()
      }
      // apply original styles, if body is overflowing(which happens on parent modal)
      const isBodyOverflowing = originalStyles.current ? originalStyles.current.isBodyOverflowing : false
      if (isBodyOverflowing) {
        const originalBodyPad = originalStyles.current ? originalStyles.current.originalBodyPad : 0
        const originalBodyOverflow = originalStyles.current ? originalStyles.current.originalBodyOverflow : 'auto'
        restoreBackgroundScrolling(originalBodyPad, originalBodyOverflow)
      }
      nodeToRestore.current = null
      originalStyles.current = null
    }
  }, [mounted])

  const handleBackdropClick: React.ReactEventHandler = React.useCallback(
    (event) => {
      if (event.target !== event.currentTarget) {
        return
      }
      if (onBackdropClick) {
        onBackdropClick()
      } else if (!disableBackdropClick && onClose) {
        onClose()
      }
    },
    [disableBackdropClick, onBackdropClick, onClose],
  )

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.key !== 'Tab') {
        return
      }
      event.stopPropagation()
      if (event.key === 'Escape' && !disableEscapeKeyDown && onClose) {
        onClose()
      }
      if (event.key === 'Tab') {
        trapFocus(rootRef.current, event)
      }
    },
    [disableEscapeKeyDown, onClose],
  )

  if (exited) {
    return null
  }

  return (
    <Portal>
      <div
        ref={rootRef}
        role='presentation'
        tabIndex={0}
        className={clsx(classes.root, className)}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {!disableBackdrop && (
          <Backdrop open={open} onClick={handleBackdropClick} transitionDuration={closeAfterDelay} />
        )}
        {children}
      </div>
    </Portal>
  )
}

export default Modal
